import {
  Alert,
  Button,
  LabeledInput,
  Modal,
  useToast,
} from '@apexinternal/ui-components'
import { useDisclosure } from '@chakra-ui/hooks'
import {
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Link,
  Icon,
} from '@chakra-ui/react'
import React, { useState } from 'react'
import { updateEmail } from '../../../../actions/updateEmail'
import { useToastErrorHandler } from '../../../../hooks'
import { FaRegEdit } from 'react-icons/fa'
import { EMAIL_ADDRESS_PATTERN } from '../../../../utils/regexConstants'

interface EmailUpdateButtonProps {
  accountNumber: string
  emailAddress?: string
}

const EmailUpdateButton = ({
  accountNumber,
  emailAddress = '',
}: EmailUpdateButtonProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [newEmail, setEmail] = useState(emailAddress)
  const [isInvalidOnBlur, setIsInvalidOnBlur] = useState(false)

  const isValidEmail = EMAIL_ADDRESS_PATTERN.test(newEmail)

  const onError = useToastErrorHandler()
  const toast = useToast()

  const handleSubmit = () => {
    onClose()
    setEmail(emailAddress)

    updateEmail(
      onError,
      () => {
        toast({
          title: 'Success!',
          description:
            'The email address has been updated. Please allow up to 24 hours for this change to take effect.',
          status: 'success',
          isClosable: true,
        })
      },
      newEmail,
      accountNumber
    )
  }

  const handleBlur = () => {
    !isValidEmail && setIsInvalidOnBlur(true)
  }

  const handleFocus = () => {
    setIsInvalidOnBlur(false)
  }

  const handleClose = () => {
    onClose()
    setEmail(emailAddress)
    setIsInvalidOnBlur(false)
  }

  return (
    <>
      <Link color="blue.500" onClick={onOpen}>
        <Icon as={FaRegEdit} marginRight="0.357rem" />
        Update
      </Link>

      <Modal isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Email</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <LabeledInput
              autoComplete="email"
              type="email"
              label="Email Address"
              isRequired
              onChange={e => setEmail(e.target.value)}
              onBlur={handleBlur}
              onFocus={handleFocus}
              isInvalid={isInvalidOnBlur}
              tabIndex={2}
              value={newEmail}
              data-testid="email-input"
            />
            <Alert
              mt={5}
              status="info"
              description={
                <Text>
                  <strong>Notice!</strong> It may take up to 24 hours to reflect
                  your changes.
                </Text>
              }
            />
          </ModalBody>

          <ModalFooter>
            <Button variant="white" mr={3} onClick={handleClose} tabIndex={1}>
              Close
            </Button>
            <Button
              variant="primary"
              isDisabled={!isValidEmail}
              onClick={handleSubmit}
            >
              Submit Update
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default EmailUpdateButton
